import {datadogLogs} from '@datadog/browser-logs';
import {Operators, type Operator} from '@lib/types/table-operators';
import {type ColumnFilter} from '@tanstack/react-table';
import {safeJsonParse} from '../utils/safe-json';
import {Base64} from 'js-base64';
import lodashIsEqual from 'lodash/isEqual';
import qs from 'qs';
import safeStringify from 'safe-stringify';
import {z} from 'zod';

/**
 * Used to delimit the object values in the query string
 */
const OBJECT_DELIMITER = `__`;
/**
 * Used to delimit the filters in the query string
 */
const METADATA_DELIMITER = `::`;
// --- Type Definitions ---
type FilterMetadata = {
  isTraversal?: boolean;
  label?: string;
  [key: string]: any;
};

const MAX_FILTER_QUERY_LENGTH = 1800;
const MAX_METADATA_LENGTH = 1000; // Max length for base64-encoded metadata

// --- localStorage Overflow Handling ---
const LOCALSTORAGE_KEY_PREFIX = `linx_filter_overflow_`;
const LOCALSTORAGE_REF_PREFIX = `__ref:`;

// Check if we're in browser environment
const isBrowser = typeof window !== `undefined`;

// Generate unique key for localStorage
const generateStorageKey = (filterId: string): string => {
  return `${LOCALSTORAGE_KEY_PREFIX}${filterId}_${Date.now()}`;
};

// Store value in localStorage and return reference key
const storeInLocalStorage = (filterId: string, value: string): string => {
  if (!isBrowser) return value;
  const storageKey = generateStorageKey(filterId);
  try {
    localStorage.setItem(storageKey, value);
    return `${LOCALSTORAGE_REF_PREFIX}${storageKey}`;
  } catch {
    return value; // Fallback if localStorage fails
  }
};

// Retrieve value from localStorage
const getFromLocalStorage = (refKey: string): string | null => {
  if (!isBrowser) return null;
  const storageKey = refKey.replace(LOCALSTORAGE_REF_PREFIX, ``);
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

// Check if value is a localStorage reference
const isLocalStorageRef = (value: string): boolean => {
  return value.startsWith(LOCALSTORAGE_REF_PREFIX);
};

// --- Configuration Constants ---
const QUERY_STRING_OPTIONS: qs.IStringifyOptions = {
  arrayFormat: `comma`,
  sort: (a, b) => (a.includes(`metadata`) ? 1 : b.includes(`metadata`) ? -1 : 0),
  skipNulls: false,
  delimiter: METADATA_DELIMITER,
  allowDots: false,
  encodeValuesOnly: true,
};

// --- Serialization ---
/**
 * Serializes the current filter state into a URL-friendly string representation.
 * @param filters - The current state of column filters.
 * @returns The serialized string representation of filters.
 */
const serialize = (filter: ColumnFilter, sizeLimit: number = MAX_FILTER_QUERY_LENGTH): string => {
  let error: FilterQueryParserError | undefined;
  let metadata: FilterMetadata | undefined;
  const parts: Array<string> = [];
  const {id, nextOperator, isTraversal, extra} = filter;

  const operator = parseOperator((filter.value as any)?.operator ?? nextOperator).replace(`$`, ``);

  const value = (filter.value as any)?.value ?? filter.nextValue;
  const serializedValue = serializeValue(value);

  let valueAsBase64 = ``;
  // If the value is too long, try as base64 encoded string
  if (typeof serializedValue === `string` && serializedValue.length > sizeLimit) {
    valueAsBase64 = Base64.encode(`${serializedValue}`);
    error = new Errors.FilterQueryTooLong(id, valueAsBase64);
  }

  parts.push(`${id}:${operator}:${valueAsBase64 || serializedValue}`);

  if (isTraversal || extra) {
    metadata = {
      ...extra,
      ...(isTraversal ? {isTraversal: true} : {}),
    };
  }

  let metadataString = ``;
  let metadataBase64 = ``;
  if (metadata) {
    metadataString = safeStringify(metadata);
    metadataBase64 = Base64.encodeURL(metadataString);

    if (metadataBase64.length > MAX_METADATA_LENGTH) {
      error = new Errors.MetadataTooLong(id, metadataString);
    } else parts.push(`metadata:${metadataBase64}`);
  }

  let output = parts.join(METADATA_DELIMITER);

  // If output exceeds limit, store in localStorage and return reference key
  if (output.length > sizeLimit) {
    const refKey = storeInLocalStorage(id, output);
    if (isLocalStorageRef(refKey)) {
      // Successfully stored in localStorage - return reference key
      return refKey;
    }
    // localStorage failed - truncate as fallback (legacy behavior)
    output = output.slice(0, sizeLimit);
  }

  if (error) throw error;

  return output;
};

// bypassing the filter parsing & ignoring the metadata splitting
const bypassFilterParsingMap = new Map<string, boolean>([[`origin_id`, true]]);

// --- Parsing ---
/**
 * Parses a query string back into the filter state.
 * @param query - The URL query string containing serialized filters.
 * @returns The parsed ColumnFiltersState.
 */
const parse = (query: string): ColumnFilter => {
  // Check if this is a localStorage reference
  if (isLocalStorageRef(query)) {
    const storedValue = getFromLocalStorage(query);
    if (storedValue) {
      // Recursively parse the actual value from localStorage
      return parse(storedValue);
    }
    // localStorage lookup failed - throw error
    throw new Errors.FilterQueryTooLong(`unknown`, query);
  }

  let error: FilterQueryParserError | undefined;
  const [filterId, operatorRaw, ...valueRaw] = query.split(`:`);
  if (bypassFilterParsingMap.get(filterId)) {
    const operator = parseOperator(`$${operatorRaw}`);
    const value = decodeURIComponent(valueRaw.join(`:`));
    return {
      id: filterId,
      value: {value, operator},
      nextValue: value,
      nextOperator: operator,
      extra: undefined,
      isTraversal: false,
    };
  }
  const [filter, metadataPart] = query.split(METADATA_DELIMITER).filter(Boolean);
  const [id, operatorString, ...rest] = filter.split(`:`);
  const operator = parseOperator(`$${operatorString}`);

  let metadata: FilterMetadata | undefined;
  if (metadataPart) {
    try {
      const [, base64] = metadataPart.split(`:`);
      const stringified = Base64.decode(base64);
      metadata = safeJsonParse(stringified, false) ?? undefined;
    } catch (error) {
      datadogLogs.logger.error(`Parse error`, {error});
    }
  }

  const valueString = rest.join(`:`);
  let value;
  try {
    value = Base64.decode(valueString);
  } catch {
    error = new Errors.FilterQueryTooLong(id, valueString);
  } finally {
    value = parseValue(valueString);
  }

  if (error?.severity === `error`) throw error;
  else if (error)
    datadogLogs.logger.warn(error.message, {
      filterId: id,
      filterValue: valueString,
      code: error.code,
    });

  return {
    id,
    value: {value, operator},
    nextValue: value,
    nextOperator: operator,
    extra: metadata || undefined,
    isTraversal: Boolean(metadata?.isTraversal),
  };
};

const isEqual = (a: ColumnFilter, b: ColumnFilter) => {
  return lodashIsEqual(a, b);
};

// --- Query Builder ---
/**
 * Creates a query builder instance for managing filter state.
 * @param initialFilters - Initial filter state.
 * @returns A query builder interface.
 */
export const createFilterQueryParser = (initialFilters?: ColumnFilter) => {
  const data = initialFilters;

  return {
    serialize,
    parse,
    isEqual,
    asState: () => data,
  };
};

// --- Operator Validation ---
/**
 * Validates and parses an operator string to ensure it's a valid Operator enum value.
 * @param operator - The operator string to parse.
 * @returns A validated Operator enum value.
 * @throws {Errors.InvalidOperator} If the operator is invalid.
 */
const parseOperator = (operator: string): Operator => {
  /**
   * @backwards-compatible We allow accepting `$${operator}` to be backwards compatible
   * with the old filter query builder, either way we return the operator with one `$`
   */
  const result = z.nativeEnum(Operators).safeParse(operator.replace(`$$`, `$`));
  if (!result.success) throw new Errors.InvalidOperator(operator, result.error.message);
  return result.data;
};

/**
 * Serializes a filter value into a string representation.
 * @param value - The value to serialize.
 * @returns The serialized value as a string.
 */
const serializeValue = (value: unknown): string | number => {
  // Handle null/undefined as empty string
  if (value === null || value === undefined) {
    return ``;
  }
  // For arrays, encode commas within individual values to avoid ambiguity with the comma delimiter.
  // Percent signs are encoded first (%25) so that existing %2C literals survive round-trip.
  if (Array.isArray(value)) {
    return value.map(v => String(v).replaceAll('%', '%25').replaceAll(',', '%2C')).join(',');
  }
  if (typeof value === `object`)
    return qs.stringify(value, {...QUERY_STRING_OPTIONS, delimiter: OBJECT_DELIMITER, arrayFormat: `indices`});
  // Encode commas in single string values too, so parseValue doesn't split them into arrays
  if (typeof value === `string`) {
    return value.replaceAll('%', '%25').replaceAll(',', '%2C');
  }
  return value as number;
};

/**
 * Checks if a string looks like JSON (starts with { or [)
 */
const looksLikeJson = (value: string): boolean => {
  const trimmed = value.trim();
  return (trimmed.startsWith(`{`) && trimmed.endsWith(`}`)) || (trimmed.startsWith(`[`) && trimmed.endsWith(`]`));
};

/**
 * Parses a serialized value back into its original form.
 * @param value - The serialized value to parse.
 * @returns The parsed value.
 */
const parseValue = (value: string): unknown => {
  const isObject = value.includes(OBJECT_DELIMITER);

  // For simple values (not objects, not arrays), return as-is without type coercion
  // This ensures text filter values like "5" stay as strings, not converted to numbers
  if (!isObject && !value.includes(',')) {
    try {
      return decodeURIComponent(value.replace(/\+/g, ` `));
    } catch {
      return value;
    }
  }

  // Handle comma-separated arrays (serialized via join(','))
  // Only split if:
  // - Contains commas
  // - No object delimiter (not a complex object)
  // - Doesn't look like JSON (wizard filters may serialize as JSON)
  if (!isObject && value.includes(`,`) && !looksLikeJson(value)) {
    return value.split(`,`).map(item => {
      // URL decode and trim each item
      try {
        return decodeURIComponent(item.trim());
      } catch {
        return item.trim();
      }
    });
  }

  const qsParse = (value: string) =>
    qs.parse(value, {
      delimiter: OBJECT_DELIMITER,
      interpretNumericEntities: true,
      parseArrays: true,
      depth: 3,
      // Parse booleans & numbers
      // https://github.com/ljharb/qs/issues/91#issuecomment-437926409
      decoder: (str, _decoder, charset) => {
        const strWithoutPlus = str.replace(/\+/g, ` `);
        if (charset === `iso-8859-1`) {
          // unescape never throws, no try...catch needed:
          return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
        }

        if (/^(\d+|\d*\.\d+)$/.test(str)) {
          return parseFloat(str);
        }

        const keywords = {
          true: true,
          false: false,
          null: null,
          undefined,
        };

        if (str in keywords) {
          return keywords[str as keyof typeof keywords];
        }

        // utf-8
        try {
          return decodeURIComponent(strWithoutPlus);
        } catch {
          return strWithoutPlus;
        }
      },
    });

  const asObject = qsParse(value);
  const primitive = qsParse(`dummy=${value}`);
  const isArray = isNaN(Number(primitive.dummy)) && Object.keys(asObject).every(key => !isNaN(Number(key)));
  if (isArray) return Object.values(asObject);

  return isObject ? asObject : primitive.dummy;
};

// --- Custom Error Classes ---
export class FilterQueryParserError extends Error {
  constructor(
    message: string,
    readonly filterId: string,
    readonly filterValue: string,
    readonly severity: `error` | `warning`,
    public readonly code: string,
  ) {
    super(message);
    this.name = `FilterQueryParserError`;
  }
}

const Errors = {
  InvalidOperator: class extends FilterQueryParserError {
    constructor(operator: string, filterId: string = `unknown`, filterValue: string = `unknown`) {
      super(
        `Invalid operator: ${operator}. This filter will be ignored.`,
        filterId,
        filterValue,
        `error`,
        `INVALID_OPERATOR`,
      );
    }
  },
  FilterQueryTooLong: class extends FilterQueryParserError {
    constructor(filterId: string, filterValue: string) {
      super(
        `Filter query is too long. This filter will be ignored`,
        filterId,
        filterValue,
        `error`,
        `FILTER_QUERY_TOO_LONG`,
      );
    }
  },
  MetadataTooLong: class extends FilterQueryParserError {
    constructor(filterId: string, filterValue: string) {
      super(
        `Metadata is too long. This filter might appear incorrectly in the UI.`,
        filterId,
        filterValue,
        `warning`,
        `METADATA_TOO_LONG`,
      );
    }
  },
};

// --- localStorage Cleanup ---
/**
 * Clean up old localStorage entries for filter overflow.
 * Call this when filters are cleared/reset to prevent localStorage from growing indefinitely.
 * @param filterId - Optional filter ID to clean up specific filter entries only
 */
export const cleanupFilterOverflowStorage = (filterId?: string): void => {
  if (!isBrowser) return;
  try {
    const keysToRemove: Array<string> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const isFilterOverflowKey = key?.startsWith(LOCALSTORAGE_KEY_PREFIX);
      const matchesFilter = !filterId || key?.includes(filterId);
      if (isFilterOverflowKey && matchesFilter) {
        keysToRemove.push(key!);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};
